<!-- eslint-disable max-lines -->
<script>
import * as AdsV3Api from '@/api/advertisement-v4';
import { mapActions, mapGetters } from 'vuex';
import { APPS } from '@/store/types';
import lang from '@/mixins/lang';
import layout from '@/mixins/layout';

import Content from '@/components/common/layout/Content.vue';
import Card from '@/components/ui/Card.vue';
import AlertWithRetry from '@/components/ui/AlertWithRetry.vue';
import Hint from '@/components/common/Hint';
import DatePickerInput from '@/components/common/DatePickerInput.vue';
import RequireAdContract from '../common/RequireAdContract.vue';
import CampaignOverviewChart from './CampaignOverviewChart.vue';
import OverviewAppCampaigns from './OverviewCampaignsTable.vue';
import storage from '@/services/storage';
import AppSelector from './AppSelector.vue';
import { createRangeUntilNow } from '@/utils/date';

const SELECTED_APP_KEY = 'ads-fav-app';
const DATE_FORMAT = 'X';

export default {
  name: 'OverviewPage',
  components: {
    RequireAdContract,
    Card,
    Hint,
    Content,
    DatePickerInput,
    CampaignOverviewChart,
    OverviewAppCampaigns,
    AlertWithRetry,
    AppSelector,
  },
  mixins: [lang, layout],
  data() {
    return {
      selectedApp: null,
      statsLoading: false,
      campaigns: [],
      series: [],
      chartError: null,
      dateLabels: [],
      chartLoading: false,
      campaignsLoading: false,
      campaignsFailed: false,
      selectedDateRange: createRangeUntilNow(1, 'weeks', DATE_FORMAT),
      downloadingReport: false,
    };
  },
  computed: {
    ...mapGetters({ apps: APPS }),
    datePickerFormat: () => DATE_FORMAT,
    datePickerRanges() {
      return [
        {
          label: this.$t('APP.STATS.FILTERS.RANGE.LAST_TWO_DAYS'),
          value: createRangeUntilNow(2, 'days'),
        },
        {
          label: this.$t('APP.STATS.FILTERS.RANGE.LAST_WEEK'),
          value: createRangeUntilNow(1, 'weeks'),
        },
        {
          label: this.$t('APP.STATS.FILTERS.RANGE.LAST_MONTH'),
          value: createRangeUntilNow(1, 'months'),
        },
        {
          label: this.$t('APP.STATS.FILTERS.RANGE.LAST_THREE_MONTHS'),
          value: createRangeUntilNow(3, 'months'),
        },
      ];
    },
  },
  watch: {
    selectedApp: {
      handler(value) {
        if (!value) return;
        storage.write(SELECTED_APP_KEY, value);
        this.getChartData();
      },
    },
    selectedDateRange: {
      handler() {
        if (!this.selectedApp) return;
        this.getChartData();
      },
    },
  },
  async created() {
    this.setPageTitle('ADVERTISEMENT.MANAGE_CAMPAIGNS.TITLE');

    if (!this.apps?.length) {
      await this.fetchAppsList();
    }

    const prevSelectedApp = storage.read(SELECTED_APP_KEY);

    const prevSelectedAppIsValid = this.apps.some(
      ({ packageName }) => prevSelectedApp === packageName,
    );

    if (prevSelectedAppIsValid) {
      this.selectedApp = prevSelectedApp;
    } else {
      this.selectedApp = this.apps[0]?.packageName;
    }
  },
  methods: {
    ...mapActions({ fetchAppsList: APPS }),
    async getChartData() {
      try {
        this.chartLoading = true;
        this.chartError = null;

        const requestParams = {
          packageName: this.selectedApp,
          start: this.selectedDateRange?.[0],
          end: this.selectedDateRange?.[1],
        };
        const { dates, stats } = await AdsV3Api.getPackageDailyStats(
          requestParams,
        );
        this.series = stats;
        this.dateLabels = dates;
      } catch (error) {
        this.chartError = error.message;
      } finally {
        this.chartLoading = false;
      }
    },
    async downloadExcel() {
      try {
        this.downloadingReport = true;
        const requestParams = {
          packageName: this.selectedApp,
          start: this.selectedDateRange?.[0],
          end: this.selectedDateRange?.[1],
        };
        await AdsV3Api.downloadOverviewStats(requestParams);
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.downloadingReport = false;
      }
    },
  },
};
</script>

<template>
  <RequireAdContract>
    <Content class="with-new-design overview-page">
      <div class="flex-row justify-spaced mb">
        <div class="flex-grow">
          <h4>{{ $t('ADVERTISEMENT.MANAGE_CAMPAIGNS.TITLE') }}</h4>
        </div>

        <div class="show-for-medium">
          <RouterLink :to="{ name: 'advertisement-create-campaign' }">
            <ElButton type="primary" icon="el-icon-circle-plus-outline">
              {{ $t('ADVERTISEMENT.NEW_CAMPAIGN') }}
            </ElButton>
          </RouterLink>
        </div>
      </div>

      <Card>
        <div class="flex-row flex-wrap">
          <div class="flex-grow">
            {{ $t('ADVERTISEMENT.OVERVIEW.SELECT_APP') }}
          </div>

          <AppSelector :selected-app.sync="selectedApp" />
        </div>
      </Card>

      <Card :mask-loading="chartLoading">
        <div slot="title" class="overview-page__card-title justify-spaced">
          <div class="flex-row gap-sm align-middle">
            <h4>{{ $t('ADVERTISEMENT.OVERVIEW.TITLE') }}</h4>
            <Hint :message="$t('HINTS.ADVERTISEMENT.OVERVIEW')" />
          </div>

          <div class="flex-row flex-sm-column gap-sm">
            <!-- format=X means unix timestamp -->
            <DatePickerInput
              v-model="selectedDateRange"
              :locale="lang"
              :ranges="datePickerRanges"
              :format="datePickerFormat"
            />

            <div>
              <ElButton
                icon="el-icon-download"
                :loading="downloadingReport"
                @click="downloadExcel"
              />
            </div>
          </div>
        </div>

        <AlertWithRetry
          v-if="chartError"
          :message="chartError"
          @retry="getChartData"
        />
        <CampaignOverviewChart v-else :series="series" :x-axis="dateLabels" />
      </Card>

      <Card>
        <template #title>
          <div class="overview-page__card-title gap-sm">
            <h4>{{ $t('ADVERTISEMENT.CAMPAIGNS.TITLE') }}</h4>
            <Hint :message="$t('HINTS.ADVERTISEMENT.OVERVIEW_CAMPAIGNS')" />
          </div>
        </template>

        <OverviewAppCampaigns :app="selectedApp" />
      </Card>
    </Content>
  </RequireAdContract>
</template>

<style lang="scss">
.overview-page {
  &__app-select {
    width: 270px;

    @include breakpoint(medium down) {
      width: 100%;
    }
  }

  &__card-title {
    display: flex;
    align-items: center;
    padding: 1.5rem;
    padding-bottom: 0.5rem;
  }

  .ui-card__title {
    border: none;
  }
}
</style>
